import React, { useEffect, useState } from 'react';

import { useLoaderData, useParams } from 'react-router-dom';

import { enqueueSnackbar } from 'notistack';

import client from '../../../../client'

import dayjs from 'dayjs'
import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Avatar, Box, Button, Checkbox, CircularProgress, Container, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Switch, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';

import i18n from '../../../../i18n';

import { UsersView } from '@/components/UsersView';
import { UsersSelect } from '@/components/UsersSelect';

const ADD_MESSAGE_MUTATION = gql`
  mutation addMessageToLead(
    $logged: Date!,
    $slug: String!,
    $text: String!,
    $status: Int!
  ) {
    addMessageToLead(
      slug: $slug,
      text: $text,
      status: $status,
      logged: $logged
    ) {
      message {
        slug
      }
    }
  }
`

const QUERY = gql`query getLead($slug: String!) {
  moodler {
    users {
      username
      firstName
      lastName
      imageUrl
      id
    }
  }
  leads {
    labels {
      slug
      name
    }
    lead(slug: $slug) {
      ...LeadFields
      phone
      contacted,
      assignees {
        id
        user {
          username
          firstName
          lastName
          imageUrl
        }
      }
      label {
        name
        slug
      }
      contractValueInEur
      messages {
        created
        slug
        status
        user {
          username
          imageUrl
        }
        text
      }
    }
  }
}`

const MUTATION = gql`mutation editLead(
  $slug: String!,
  $isBlocked: Boolean,
  $notes: String,
  $email: String,
  $userIds: [ID!],
  $contacted: Date,
  $shouldContact: Date,
  $insight: String,
  $spotifyMonthlyListeners: Int,
  $socials: String,
  $labelSlug: String
) {
  editLead(
    slug: $slug,
    notes: $notes,
    isBlocked: $isBlocked,
    email: $email,
    contacted: $contacted, 
    shouldContact: $shouldContact,
    spotifyMonthlyListeners: $spotifyMonthlyListeners,
    userIds: $userIds,
    insight: $insight,
    labelSlug: $labelSlug,
    socials: $socials
  ) {
    lead {
      slug
    }
  }
}`

const MARK_LEAD_AS_CONTACTED_MUTATION = gql`
  mutation markLeadAsContacted(
    $leadId: ID!
  ) {
    markLeadAsContacted(
      leadId: $leadId
    ) {
      lead {
        id
        name
        slug
      }
    }
  }
`

export function EditLeadOverviewPage() {
  const { slug } = useParams() 
  return <EditLeadOverview slug={slug} />
}

export function EditLeadOverview({
  onUpdated = () => {},
  slug
}) {
  
  const [newMessage, setNewMessage] = useState('')

  const [status, setStatus] = useState(0)

  const { data, loading, refetch } = useQuery(QUERY, {
    variables: {
      slug
    }
  })

  const users = data?.moodler?.users
  const lead = data?.leads?.lead
  const labels = data?.leads?.labels

  const [
    updateLead,
    {
      loading: updating,
      data: updateLeadData,
      error: updateError
    }
  ] = useMutation(
    MUTATION, {
      onCompleted: () => {
        enqueueSnackbar(i18n.t('Lead updated'), { variant: 'success' })
      },
      onError: () => {
        enqueueSnackbar(i18n.t('Sorry, an error occurred when updating the lead'), { variant: 'error' })
      }
    }
  )
  const [labelSlug, setLabelSlug] = useState(lead?.label?.slug) 
  const [notes, setNotes] = useState(lead?.notes || '')
  const [selectedUserIds, setSelectedUserIds] = useState(lead?.assignees?.map(a => a?.user?.username) ?? [])
  const [contacted, setContacted] = useState(null)
  const [shouldContact, setShouldContact] = useState(null)
  const [email, setEmail] = useState(lead?.email || '')
  const [phone, setPhone] = useState(lead?.phone || '')
  const [isBlocked, setBlocked] = useState(lead?.isBlocked)
  const [insight, setInsight] = useState('')
  const [socials, setSocials] = useState('')
  const [spotifyMonthlyListeners, setSpotifyMonthlyListeners] = useState(0)

  useEffect(() => {
    setNotes(lead?.notes)
    setSocials(lead?.socials)
    if (users) {
      setSelectedUserIds(lead?.assignees?.map(a => a?.user?.username) ?? [])
    }
    setLabelSlug(lead?.label?.slug)
    setStatus(lead?.status)
    setInsight(lead?.insight)
    setEmail(lead?.email)
    setPhone(lead?.phone)
    setBlocked(lead?.isBlocked)
    setSpotifyMonthlyListeners(lead?.monthlyListeners ?? 0)
    if(lead?.contacted) {
      setContacted(dayjs(lead?.contacted))
    }
    if (lead?.shouldContact) {
      setShouldContact(dayjs(lead?.shouldContact))
    }
    setTimeout(() => {
    //  setLoading(false)
    }, 1000)
  }, [lead])
 
  const [
    markLeadAsContacted,
    {
      loading: marking
    }
  ] = useMutation(
    MARK_LEAD_AS_CONTACTED_MUTATION,
    {
      variables: {
        leadId: lead?.id
      },
      onCompleted: () => {
        refetch()
      }
    }
  )
 
  const handleSubmit = (event) => {
    event.preventDefault()
    updateLead({
      variables: {
        slug: lead.slug,
        isBlocked: isBlocked ?? false,
        notes: notes ?? '',
        userIds: selectedUserIds,
        email: email ?? '',
        phone,
        spotifyMonthlyListeners: parseInt(spotifyMonthlyListeners),
        contacted: contacted?.format('YYYY-MM-DD'),
        shouldContact: shouldContact?.format('YYYY-MM-DD'),
        insight,
        socials
      }
    }).then(() => {
      /*refetch().then(() => {
        onUpdated()
      })*/
    })
  }
  return (
    <form onSubmit={handleSubmit}>      
      <Box sx={{ padding: 1}}>
        {
          loading ? 
            <Box sx={{ aspectRatio: '16/9', alignItems: 'center', justifyContent: 'center', display: 'flex', gap: 1, flexDirection: 'column' }}>
              <CircularProgress />
              <Typography variant="h6">{i18n.t('Loading')}</Typography>
            </Box>
          :
            <>
            <fieldset>
              <legend>{i18n.t('Contact details')}</legend>
                <Paper sx={{padding: 1, opacity: isBlocked ? 0.5 : 1, flexDirection: 'column'}}>
                  {/*<TextField
                    multiline
                    fullWidth
                    variant="standard"
                    label="Name"
                    readOnly
                    value={lead?.name} 
                  />*/}
                  <TextField
                    type="email"
                    multiline
                    fullWidth
                    label={i18n.t("E-mail")}
                    variant="standard"
                    onChange={(event) => setEmail(event.target.value)}
                    value={email} 
                  />
                  <TextField
                    type="text"
                    multiline
                    fullWidth
                    label={i18n.t("phone")}
                    variant="standard"
                    onChange={(event) => setPhone(event.target.value)}
                    defaultValue={phone} 
                  />
                  <TextField
                    multiline
                    fullWidth
                    variant="standard"
                    label={i18n.t("Socials")}
                    defaultValue={socials}
                    onChange={(event) => setSocials(event.target.value)}
                  />
                </Paper>
              </fieldset>
              <fieldset>
                <legend>{i18n.t('Label')}</legend>
                <Paper>
                  <FormControl fullWidth>
                    <InputLabel>{i18n.t('Label')}</InputLabel>
                    <Select
                      variant="outlined"
                      value={labelSlug}
                      defaultValue={labelSlug}
                      label={i18n.t("Label")}
                      sx={{
                        width: '100%'
                      }}
                      onChange={(event) => {
                        setLabelSlug(event.target.value)
                      }}
                    >
                      {labels.map(subLabel => (
                        <MenuItem
                          key={subLabel.slug}
                          value={subLabel.slug}
                        >
                          {i18n.t(subLabel.name)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </fieldset>
              <fieldset>
                <legend>{i18n.t('spotify')}</legend>
                <Paper>
                  <TextField
                    type="number"
                    fullWidth
                    label={i18n.t("monthly-listeners")}
                    variant="standard"
                    onChange={(event) => setSpotifyMonthlyListeners(event.target.value)}
                    value={spotifyMonthlyListeners} 
                  />
                </Paper>
              </fieldset>
              <fieldset>
                <legend>{i18n.t('Notes')}</legend>
                <Paper sx={{padding: 1, opacity: isBlocked ? 0.5 : 1, flexDirection: 'column'}}>
              
                  <TextField
                    multiline
                    fullWidth
                    variant="standard"
                    label={i18n.t("Notes")}
                    defaultValue={notes}
                    onChange={(event) => setNotes(event.target.value)}
                  />
                </Paper>
              </fieldset> 
              <fieldset>
                <legend>{i18n.t('Assignment')}</legend>
                <Paper sx={{padding: 1, opacity: isBlocked ? 0.5 : 1, flexDirection: 'column'}}>
                  <FormControl sx={{ minWidth: 220 }}>
                    <InputLabel>{i18n.t('assigned-to')}</InputLabel>
                    <UsersSelect
                      users={users}
                      selectedUserIds={selectedUserIds}
                      onChange={(value) => setSelectedUserIds(value)}
                    />
                  </FormControl>
                </Paper>
              </fieldset>
              <fieldset>
                <legend>{i18n.t('Contact')}</legend>
                <Paper sx={{padding: 1, opacity: isBlocked ? 0.5 : 1, flexDirection: 'column'}}>
                  <LoadingButton
                    variant={!!contacted ? 'outlined' : 'contained'}
                    onClick={() => markLeadAsContacted({ variables: { leadId: lead?.slug }})}
                    loading={marking}
                  >
                    {contacted ? i18n.t('marked-as-contacted-at', { contacted: dayjs(contacted).format('YYYY-MM-DD') }) : i18n.t('mark-as-contacted')}
                  </LoadingButton>
                </Paper>
              </fieldset>
              <fieldset>
                <legend>{i18n.t('Contract value')}</legend>
                <Paper sx={{opacity: isBlocked ? 0.5 : 1, flexDirection: 'column'}}>        
                  <TextField
                    label={i18n.t('Contract Value')}
                    value={lead.contractValueInEur?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'EUR'}) ?? '-'}
                    sx={{ textAlign: 'right' }}
                    readOnly
                  />
                </Paper>
              </fieldset>
            </>
          }
      </Box>
      <Box sx={{ marginTop: 1, alignItems: 'center', display: 'flex', flexDirection: 'row', gap: 2}}>
        <LoadingButton loading={updating} type="submit" variant="contained">{i18n.t('Save')}</LoadingButton>
      </Box>
    </form>
  )
}

EditLeadOverview.loader = async ({ params: { slug }}) => {
  const { data } = await client.query({
    query: QUERY,
    variables: {
      slug
    }
  })
  return {
    labels: data.leads.labels,
    lead: data.leads.lead,
    users: data.moodler.users 
  }
}